
import { Investor, Contact } from "@/types/investor";
import { supabase } from "@/integrations/supabase/client";

// Check if we're in the middle of a logout operation
let globalLogoutInProgress = false;

// Function to set the logout flag - called during logout
export const setLogoutInProgress = (inProgress: boolean) => {
  globalLogoutInProgress = inProgress;
  console.log(`Logout in progress flag set to: ${inProgress}`);
};

/**
 * Load data from Supabase using the new individual records structure
 */
export const loadFromSupabaseThenLocal = async (userId: string) => {
  try {
    if (globalLogoutInProgress) {
      console.warn("Blocking database load attempt during logout");
      return { investors: [], contacts: [], processedIds: new Set<string>(), source: 'none' };
    }
    
    console.log("Loading data from Supabase for user:", userId);
    
    // Load investors from individual records
    const { data: investorsData, error: investorsError } = await supabase
      .from('investors')
      .select('*')
      .eq('user_id', userId);
      
    if (investorsError) {
      console.error("Error loading investors from Supabase:", investorsError);
      // Try to load from legacy table as fallback
      return loadFromLegacyTable(userId);
    }
    
    // Load contacts from individual records
    const { data: contactsData, error: contactsError } = await supabase
      .from('contacts')
      .select('*')
      .eq('user_id', userId);
      
    if (contactsError) {
      console.error("Error loading contacts from Supabase:", contactsError);
      // Try to load from legacy table as fallback
      return loadFromLegacyTable(userId);
    }
    
    // Transform database records to application model
    const investors = investorsData.map(transformDatabaseInvestor);
    const contacts = contactsData.map(transformDatabaseContact);
    
    // Create processedIds set based on which investors have been enriched
    const processedIds = new Set<string>();
    if (investors && investors.length > 0) {
      investors.forEach(investor => {
        if (investor.enrichedWith) {
          processedIds.add(investor.id);
        }
      });
      
      console.log(`Loaded ${investors.length} investors from database, ${processedIds.size} are processed`);
    }

    return { 
      investors, 
      contacts, 
      processedIds,
      source: 'database' 
    };
    
  } catch (error) {
    console.error("Error in loadFromSupabaseThenLocal:", error);
    // Try to load from legacy table as fallback
    return loadFromLegacyTable(userId);
  }
};

/**
 * Fallback function to load from legacy JSON array table
 */
const loadFromLegacyTable = async (userId: string) => {
  try {
    console.log("Attempting to load from legacy investor_data table");
    
    // Load from legacy table
    const { data: investorData, error: investorError } = await supabase
      .from('investor_data')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
      
    if (investorError) {
      console.error("Error loading from legacy table:", investorError);
      return { investors: [], contacts: [], processedIds: new Set<string>(), source: 'none' };
    }
    
    if (investorData) {
      // We have data from the legacy table
      console.log("Found investor data in legacy table");
      
      // Type assertion to handle Supabase typing issues
      const investors = investorData.investors as unknown as Investor[];
      const contacts = investorData.contacts as unknown as Contact[];
      
      // Create processedIds set based on which investors have been enriched
      const processedIds = new Set<string>();
      if (investors && investors.length > 0) {
        investors.forEach(investor => {
          if (investor.enrichedWith) {
            processedIds.add(investor.id);
          }
        });
        
        console.log(`Loaded ${investors.length} investors from legacy table, ${processedIds.size} are processed`);
      }

      // Migrate data to the new structure
      console.log("Initiating migration to the new structure");
      await supabase.rpc('run_data_migration');
      console.log("Data migration initiated");

      return { 
        investors, 
        contacts, 
        processedIds,
        source: 'legacy_table' 
      };
    } else {
      // No data in legacy table
      console.log("No investor data found in legacy table");
      return { investors: [], contacts: [], processedIds: new Set<string>(), source: 'none' };
    }
  } catch (error) {
    console.error("Error in loadFromLegacyTable:", error);
    return { investors: [], contacts: [], processedIds: new Set<string>(), source: 'none' };
  }
};

/**
 * Transform database investor record to application model
 */
const transformDatabaseInvestor = (dbInvestor: any): Investor => {
  return {
    id: dbInvestor.original_id,
    name: dbInvestor.name || '',
    firm: dbInvestor.firm || '',
    relevanceScore: dbInvestor.relevance_score || 0,
    keywords: dbInvestor.keywords || [],
    portfolioCompanies: dbInvestor.portfolio_companies || [],
    investmentFocus: dbInvestor.investment_focus || '',
    website: dbInvestor.website || '',
    contactCount: dbInvestor.contact_count || 0,
    matchedContactIds: dbInvestor.matched_contact_ids || [],
    lastRelevanceUpdate: dbInvestor.last_relevance_update || null,
    confidence: dbInvestor.confidence || 'medium',
    notes: dbInvestor.notes || '',
    sources: dbInvestor.sources || [],
    enrichedWith: dbInvestor.enriched_with,
    enrichmentFailed: dbInvestor.enrichment_failed || false,
    location: dbInvestor.location || {}
  };
};

/**
 * Transform database contact record to application model
 */
const transformDatabaseContact = (dbContact: any): Contact => {
  return {
    id: dbContact.original_id,
    firmName: dbContact.firm_name || '',
    name: dbContact.name || '',
    role: dbContact.role || '',
    email: dbContact.email || '',
    linkedin: dbContact.linkedin || '',
    twitter: dbContact.twitter || '',
    notes: dbContact.notes || '',
    phone: dbContact.phone || '',
    location: dbContact.location || '',
    firstName: dbContact.first_name || '',
    lastName: dbContact.last_name || '',
    boardSeats: dbContact.board_seats || 0,
    dealRoles: dbContact.deal_roles || 0,
    university: dbContact.university || '',
    gender: dbContact.gender || ''
  };
};

/**
 * Transform application investor model to database record
 */
const transformToDbInvestor = (investor: Investor, userId: string): any => {
  return {
    original_id: investor.id,
    name: investor.name,
    firm: investor.firm,
    relevance_score: investor.relevanceScore,
    keywords: investor.keywords,
    portfolio_companies: investor.portfolioCompanies,
    investment_focus: investor.investmentFocus,
    website: investor.website,
    matched_contact_ids: investor.matchedContactIds,
    last_relevance_update: investor.lastRelevanceUpdate,
    confidence: investor.confidence,
    notes: investor.notes,
    sources: investor.sources,
    enriched_with: investor.enrichedWith,
    enrichment_failed: investor.enrichmentFailed,
    location: investor.location,
    user_id: userId
  };
};

/**
 * Transform application contact model to database record
 */
const transformToDbContact = (contact: Contact, userId: string, investorId?: string): any => {
  return {
    original_id: contact.id,
    firm_name: contact.firmName,
    name: contact.name,
    role: contact.role,
    email: contact.email,
    linkedin: contact.linkedin,
    twitter: contact.twitter,
    notes: contact.notes,
    phone: contact.phone,
    location: contact.location,
    first_name: contact.firstName,
    last_name: contact.lastName,
    board_seats: contact.boardSeats,
    deal_roles: contact.dealRoles,
    university: contact.university,
    gender: contact.gender,
    investor_id: investorId,
    user_id: userId
  };
};

/**
 * Save to Supabase using the new individual records structure
 */
export const saveToSupabaseThenLocal = async (
  investors: Investor[],
  contacts: Contact[],
  processedIds: Set<string>,
  forceSync: boolean = false
): Promise<boolean> => {
  try {
    if (globalLogoutInProgress) {
      console.warn("Blocking database save attempt during logout");
      return false;
    }
    
    // Enhanced validation:
    // 1. Log detailed information about the data being saved
    const processedCount = investors.filter(inv => inv.enrichedWith).length;
    console.log(`Saving to database: ${investors.length} investors (${processedCount} processed), ${contacts.length} contacts`);
    
    // 2. Never save empty investor arrays - this is a critical protection
    if (!investors || investors.length === 0) {
      console.warn("BLOCKED SAVE: Empty investors array detected - preventing potential data loss");
      return false;
    }
    
    // 3. Check if we're trying to save data with fewer enriched investors than expected
    if (processedIds.size > processedCount) {
      console.warn(`INTEGRITY WARNING: ProcessedIds set has ${processedIds.size} entries but only ${processedCount} investors have enrichedWith property`);
    }
    
    // Get the current user session
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    
    // Skip Supabase if no userId (not authenticated)
    if (!userId) {
      console.log("No authenticated user, cannot save data");
      return false; 
    }

    // Batch save implementation
    const BATCH_SIZE = 100;  // Maximum number of records per batch
    
    // Prepare investor batches
    const investorBatches = [];
    for (let i = 0; i < investors.length; i += BATCH_SIZE) {
      investorBatches.push(investors.slice(i, i + BATCH_SIZE));
    }
    
    // Keep track of investor DB IDs for contact association
    const investorDbIds = new Map<string, string>();
    
    // Process each investor batch
    for (const batch of investorBatches) {
      // Transform investors to DB records
      const dbInvestors = batch.map(investor => transformToDbInvestor(investor, userId));
      
      // Create a lookup to match original_id with database id 
      // Fix: Remove the 'returning' property and use the 'select' method instead
      const { data: insertedInvestors, error: batchError } = await supabase
        .from('investors')
        .upsert(dbInvestors, { 
          onConflict: 'original_id,user_id'
        })
        .select('id, original_id');
      
      if (batchError) {
        console.error("Error batch inserting investors:", batchError);
        continue;
      }
      
      // Fix: Add null check for insertedInvestors before proceeding
      if (insertedInvestors) {
        // Map original investor IDs to database IDs
        insertedInvestors.forEach(inv => {
          investorDbIds.set(inv.original_id, inv.id);
        });
      }
    }
    
    // Prepare contact batches with investor DB IDs
    const contactBatches = [];
    for (let i = 0; i < contacts.length; i += BATCH_SIZE) {
      contactBatches.push(contacts.slice(i, i + BATCH_SIZE));
    }

    // Process each contact batch
    for (const batch of contactBatches) {
      // Find associated investor for each contact
      const dbContacts = batch.map(contact => {
        // Find the matching investor based on firm name
        const matchingInvestor = investors.find(i => 
          i.firm && contact.firmName && 
          i.firm.toLowerCase() === contact.firmName.toLowerCase()
        );
        
        // Get database ID for the investor if there's a match
        const investorDbId = matchingInvestor ? 
          investorDbIds.get(matchingInvestor.id) : undefined;
        
        return transformToDbContact(contact, userId, investorDbId);
      });
      
      // Batch save contacts
      const { error: contactsError } = await supabase
        .from('contacts')
        .upsert(dbContacts, {
          onConflict: 'original_id,user_id'
        });
      
      if (contactsError) {
        console.error("Error batch inserting contacts:", contactsError);
      }
    }
    
    console.log(`Successfully saved ${investors.length} investors and ${contacts.length} contacts to Supabase`);
    return true;
  } catch (error) {
    console.error("Error in saveToSupabase:", error);
    return false;
  }
};

/**
 * Clear all localStorage entries that might contain investor data
 * Used during logout and version updates
 */
export const clearInvestorLocalStorage = (): void => {
  // First, save API keys that we want to preserve
  const openaiToken = localStorage.getItem("openai_token");
  const perplexityToken = localStorage.getItem("perplexity_token");
  
  // List keys to remove - specifically excluding API keys
  const keysToRemove = [
    "investors", 
    "contacts", 
    "businessInfo",
    "relevanceContext",
    "emailDrafts",
    "sentEmails",
    "investors_data", 
    "contacts_data", 
    "processed_investor_ids",
    "batch_progress",
    "batch_completion",
    "processing_state",
    "last_activity"
  ];
  
  // Log which keys we're operating on
  console.log("Clearing investor data from localStorage, preserving API keys");
  
  // Remove all the keys in our removal list
  keysToRemove.forEach(key => {
    if (localStorage.getItem(key)) {
      console.log(`Removing localStorage item: ${key}`);
      localStorage.removeItem(key);
    }
  });
  
  // Restore the API keys we want to keep
  if (openaiToken) {
    localStorage.setItem("openai_token", openaiToken);
    console.log("Preserved OpenAI API key");
  }
  
  if (perplexityToken) {
    localStorage.setItem("perplexity_token", perplexityToken);
    console.log("Preserved Perplexity API key");
  }
  
  console.log("Completed clearing localStorage while preserving API keys");
};
